/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, isCurrentOwnerPage, resolveOwnerSlug } from "@cosmos/hygraph-data";
import {
	EntityTypeName,
	EntityWhereInput,
	Locale,
	PageSectionsFragment,
	RefSectionsEntitiesFragment,
	RefSectionsFragment,
} from "@cosmos/hygraph-schema";

import { getProjectModelIds, ProjectModelIds } from "./modelIds";

/** Static values a consumer needs to build Hygraph Studio deep links. Consistent per site. */
export interface HygraphPageSectionsApiConfig {
	studioBaseUrl: string;
	projectId: string;
	environmentId: string;
}

/** One section on a page, in layout order. See API-SPEC-page-sections.md. */
export interface PageSectionInfo {
	index: number;
	typename: string;
	/** The section's variant when it exposes one (e.g. `simpleCtaVariant`); null otherwise. */
	variant: string | null;
	displayName: string;
	tags: string[];
	/** True when the section arrives via a `CosmosReferenceSection` (CMS-managed body section). */
	isRefSection: boolean;
	/** True when the section is an Legal Content inline section CK Editor/CK Editor Collapsed element */
	isInlineSection: boolean;
	/** True when the section is an article-body component (e.g. a blog/plugin `articleComponents` entry). */
	isArticleSection: boolean;
	hygraphEntryId: string | null;
	hygraphModelId: string | null;
}

/** A section already resolved on its page query (inline union member or article component). */
export type ResolvedSection = { __typename?: string | null; id?: string | null } & Record<string, unknown>;

export interface PageSectionsPageInfo {
	slug: string;
	locale: string;
	app: string;
	templateName: string;
	hygraphEntryId: string | null;
	hygraphModelId: string | null;
}

/** Minimal hero shape shared by every template's `hero` field (an entity stub or a resolved hero). */
export type HeroEntityStub = { __typename?: string | null; id?: string | null } | null | undefined;

/**
 * A page's structural content, normalized across the different Cosmos page templates so the route
 * can build its response the same way regardless of which template served the slug.
 */
export interface NormalizedSectionsPage {
	id: string | null;
	typename: string;
	slug: string | null;
	/** Hero referenced directly on the page; surfaced as the first section. */
	hero: HeroEntityStub;
	/** `CosmosReferenceSection` stubs needing a per-section detail fetch (see getPageSectionsDetails). */
	refSectionEntities: RefSectionsEntitiesFragment[];
	/** Inline union sections already resolved on the page query (e.g. legal-content blocks). */
	inlineSections: ResolvedSection[];
	/** Article-body components already resolved on the page query (blog/plugin detail). */
	articleSections: ResolvedSection[];
}

export interface PageSectionsResult {
	hygraph: HygraphPageSectionsApiConfig;
	page: PageSectionsPageInfo;
	sections: PageSectionInfo[];
}

/**
 * Humanize a section typename into a display name by replacing the `Cosmos` prefix
 * with 🌌, tagging the kind (`Page`/`Section`/`Hero`) as `[page]`/`[section]`/`[hero]`,
 * and spacing camelCase boundaries: `CosmosSectionFeatureCard` → `🌌 [section] Feature Card`.
 * This is a mechanical approximation of the CMS's curated names (open question #3 in the
 * spec) — good enough for v0.1, deterministic, and free of a hand-maintained map.
 */
export const humanizeSectionTypename = (typename: string): string =>
	`${typename
		.replace(/^Cosmos(Page|Section|Hero|Element)?/, (_match, kind: string | undefined) =>
			kind ? `🌌 [${kind.toLowerCase()}] ` : "🌌 ",
		)
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.trim()} (fallback)`;

/**
 * Resolves a section/hero/page typename to its curated CMS display name and Hygraph model id,
 * from the project's generated model-id lookups (see modelIds.ts). The section mappers take one
 * of these so the same instance is reused across a page's sections.
 */
export interface SectionModelLookup {
	/** Curated CMS display name for a typename, falling back to `humanizeSectionTypename` when unmapped. */
	displayName: (typename: string) => string;
	/** Hygraph model id for a typename, or `null` when the project has no id for it. */
	modelId: (typename: string) => string | null;
	projectModelIds: ProjectModelIds | undefined;
}

/**
 * Build a `SectionModelLookup` for a Hygraph project (by project id). Display names come from the
 * project's `lookupDisplayName` — falling back to `humanizeSectionTypename` for typenames it doesn't
 * list — and model ids from its `lookupModelId`. With no/unknown project id every typename falls
 * back (humanized name, `null` model id).
 */
export const createSectionModelLookup = (projectId?: string): SectionModelLookup => {
	const projectModelIds = getProjectModelIds(projectId);
	return {
		displayName: (typename) => projectModelIds?.lookupDisplayName[typename] ?? humanizeSectionTypename(typename),
		modelId: (typename) => projectModelIds?.lookupModelId[typename] ?? null,
		projectModelIds,
	};
};
/**
 * Format a stored 32-char hex project id into a hyphenated UUID (the form Hygraph
 * Studio URLs use). Passes through anything that isn't a bare 32-hex string.
 */
export const formatProjectId = (raw: string): string => {
	const hex = raw.replace(/-/g, "");
	if (!/^[0-9a-f]{32}$/i.test(hex)) {
		return raw;
	}
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Build the standard `HygraphPageSectionsApiConfig` for a sections route from the site's environment,
 * centralizing the env-var fallback chain every route otherwise repeats. Reads `process.env` at
 * call time and formats the project id into the hyphenated UUID Studio deep links use — so a change
 * to the variables or their precedence lives in one place. Multi-site setups that resolve config
 * per request (e.g. the cosmos website via `getSite`) build their config directly instead.
 */
export const buildHygraphPageSectionsApiConfig = (): HygraphPageSectionsApiConfig => ({
	studioBaseUrl: process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT || process.env.HYGRAPH_ENDPOINT || "",
	projectId: formatProjectId(process.env.HYGRAPH_APP_PROJECT_ID || ""),
	environmentId: process.env.HYGRAPH_ENVIRONMENT || "",
});

/** `CosmosConfigSection` fields surfaced as tags, in the spec's declared order. */
const SECTION_CONFIG_TAG_KEYS = [
	"edsBackgroundColor",
	"backgroundFullHeight",
	"collapsed",
	"backgroundAnimation",
	"disablePaddingBottom",
	"backgroundOverlayOverride",
	"backgroundLayoutOverride",
] as const;

/** Boolean flags on a section heading (SectionHeading / IconSectionHeading) surfaced as tags. */
const HEADING_BOOLEAN_TAG_KEYS = ["compressedTitle", "centeredVerticalStack"] as const;

/** Humanize an element typename: `CosmosElementIconSectionHeading` → `IconSectionHeading`. */
const humanizeHeadingType = (typename: string): string => typename.replace(/^CosmosElement/, "");

/**
 * Append a tag for one property, skipping defaults. A boolean `true` contributes its bare
 * key name; any other set value contributes `key:value`. Nullish, `false`, and empty-string
 * values are omitted so tags only reflect what's actually on.
 */
const pushTag = (tags: string[], key: string, value: unknown): void => {
	if (value === true) {
		tags.push(key);
	} else if ((typeof value === "string" && value !== "") || typeof value === "number") {
		tags.push(`${key}:${value}`);
	}
};

/** Find a section's variant value, reading the aliased `*Variant` key it exposes (if any). */
export const extractVariant = (section: PageSectionsFragment): string | null => {
	const record = section as Record<string, unknown>;
	for (const key of Object.keys(record)) {
		if (!/variant/i.test(key)) continue;
		const value = record[key];
		if (typeof value === "string" && value !== "") {
			return `${key}:${value}`;
		}
		if (typeof value === "number") {
			return `${key}:${String(value)}`;
		}
	}
	return null;
};

/**
 * Collapse a section's variant, heading, and config into a flat, homogenized list of tags —
 * one entry per property that is enabled (`true`) or set to a non-default value (`key:value`).
 * Variant keys are aliased per section type (e.g. `simpleCtaVariant`), so we match any
 * top-level `*Variant` field rather than maintain a per-type map. An empty array means
 * "all defaults".
 */
export const buildSectionTags = (
	section: PageSectionsFragment | null | undefined,
	variant?: string | null,
): string[] => {
	const tags: string[] = [];
	if (!section) {
		return tags;
	}
	const record = section as Record<string, unknown>;

	if (variant !== null && typeof variant !== "undefined") {
		pushTag(tags, variant, true);
	}

	const config = record.config as Record<string, unknown> | null | undefined;
	if (config) {
		const heading = config.heading as Record<string, unknown> | null | undefined;
		if (heading?.__typename) {
			pushTag(tags, "heading", humanizeHeadingType(heading.__typename as string));
			for (const key of HEADING_BOOLEAN_TAG_KEYS) {
				pushTag(tags, key, heading[key]);
			}
		}
		for (const key of SECTION_CONFIG_TAG_KEYS) {
			pushTag(tags, key, config[key]);
		}
	}

	return tags;
};

/**
 * Map resolved ref sections (each a CosmosReferenceSection wrapping a concrete section with
 * its full config/heading/variant) into the response shape. Reference sections with no
 * resolvable inner section are skipped; `index` reflects position among the emitted sections.
 */
export const mapRefSectionsToSections = (
	refSections: RefSectionsFragment[] | undefined | null,
	lookup: SectionModelLookup,
): PageSectionInfo[] => {
	const sections: PageSectionInfo[] = [];

	for (const ref of refSections ?? []) {
		const section = ref?.section;
		if (!section?.__typename) {
			continue;
		}
		const variant = extractVariant(section);
		sections.push({
			index: sections.length,
			typename: section.__typename,
			variant,
			// We are using section.__typename instead of ref.__typename for more visible type
			displayName: lookup.displayName(section.__typename),
			tags: buildSectionTags(section, variant),
			// Body sections always arrive via a CosmosReferenceSection, so they are CMS-managed.
			isRefSection: true,
			isInlineSection: false,
			isArticleSection: false,
			hygraphEntryId: ref.id ?? null,
			hygraphModelId: lookup.modelId(ref.__typename),
		});
	}

	return sections;
};

/**
 * Map a page's `hero` (a `CosmosHero*` referenced directly on the page, not via a
 * `CosmosReferenceSection`) into a `PageSectionInfo` so it can be surfaced as the first
 * entry in the sections list. Returns `null` when the page has no hero. `index` is left at
 * 0 here; callers that prepend it should reindex the combined list.
 */
export const mapHeroToSection = (hero: HeroEntityStub, lookup: SectionModelLookup): PageSectionInfo | null => {
	if (!hero?.__typename) {
		return null;
	}
	return {
		index: 0,
		typename: hero.__typename,
		variant: null,
		displayName: lookup.displayName(hero.__typename),
		tags: [],
		// The hero is referenced directly on the page, not through a CosmosReferenceSection.
		isRefSection: false,
		isInlineSection: false,
		isArticleSection: false,
		hygraphEntryId: hero.id ?? null,
		hygraphModelId: lookup.modelId(hero.__typename),
	};
};

/**
 * Map sections that are already resolved on their page query (they carry their concrete typename and,
 * where applicable, config/variant) into `PageSectionInfo`. Unlike ref sections these need no
 * per-section detail fetch. Covers both a page's inline union sections (e.g. legal-content blocks) and
 * its article-body components; the caller flags which via `isArticleSection`. Entries with no typename
 * are skipped and `index` reflects position among the emitted sections; callers that combine lists
 * should reindex the result.
 */
const mapResolvedSections = (
	resolvedSections: PageSectionsFragment[] | null | undefined,
	options: { isArticleSection?: boolean; isInlineSection?: boolean },
	lookup: SectionModelLookup,
): PageSectionInfo[] => {
	const sections: PageSectionInfo[] = [];

	for (const section of resolvedSections ?? []) {
		if (!section?.__typename) {
			continue;
		}

		const variant = extractVariant(section);
		sections.push({
			index: sections.length,
			typename: section.__typename,
			variant,
			displayName: lookup.displayName(section.__typename),
			tags: buildSectionTags(section, variant),
			isRefSection: false,
			isInlineSection: !!options.isInlineSection,
			isArticleSection: !!options.isArticleSection,
			hygraphEntryId: section.id ?? null,
			hygraphModelId: lookup.modelId(section.__typename),
		});
	}

	return sections;
};

/** Map a page's inline union sections (e.g. legal-content rich-text blocks) into the response shape. */
export const mapInlineSectionsToSections = (
	inlineSections: ResolvedSection[] | null | undefined,
	lookup: SectionModelLookup,
): PageSectionInfo[] =>
	mapResolvedSections(inlineSections as PageSectionsFragment[], { isInlineSection: true }, lookup);

/** Map a page's article-body components (blog/plugin `articleComponents`) into the response shape. */
export const mapArticleSectionsToSections = (
	articleSections: ResolvedSection[] | null | undefined,
	lookup: SectionModelLookup,
): PageSectionInfo[] =>
	mapResolvedSections(articleSections as PageSectionsFragment[], { isArticleSection: true }, lookup);

/**
 * Fetch the full section detail (config, heading, variant) for a page's ref-section entities.
 * Mirrors the per-section fan-out the page renderer uses — `CosmosPageRefSection` resolves each
 * concrete section via the `RefSections` union — but skips external-content enrichment, since
 * this API needs only the structural config. One query per section, run in parallel; order is
 * preserved and sections that fail to resolve are dropped.
 */
export const getPageSectionsDetails = async (
	refSectionsEntities: RefSectionsEntitiesFragment[] | undefined | null,
	hygraphLocale: string,
): Promise<RefSectionsFragment[]> => {
	const refs = (refSectionsEntities ?? []).filter((ref) => ref?.section?.__typename);

	if (!refs.length) {
		return [];
	}

	const locale = [hygraphLocale, Locale.En] as Locale[];
	const responses = await Promise.all(
		refs.map(async (ref) => {
			const entity = {
				id: ref.id,
				stage: ref.stage,
				typename: EntityTypeName.CosmosReferenceSection,
			} as EntityWhereInput;
			const data = await getHygraphCosmosPageData(
				"CosmosPageRefSection",
				{ entities: [entity], locale },
				undefined,
			).catch((e) => {
				// Log which section dropped and why so a partial-load post-mortem can trace it. The section
				// is still omitted from the response; the reindexed list silently loses this entry.
				console.error("[Page Sections] section detail fetch failed", {
					entityId: ref.id,
					sectionTypename: ref.section?.__typename ?? null,
					error: e instanceof Error ? e.message : String(e),
				});
				return undefined;
			});
			if (!data?.entities) return null;
			const [first] = data.entities;
			return { ...entity, ...first };
		}),
	);

	const refSections: RefSectionsFragment[] = [];
	for (const res of responses) {
		if (res?.__typename === "CosmosReferenceSection" && res.section) {
			refSections.push({ __typename: "CosmosReferenceSection", id: res.id, section: res.section });
		}
	}

	return refSections;
};

/** Names of the Cosmos page-content SDK queries this route fetches. A literal subset of the SDK. */
type PageContentQuery =
	| "CosmosPageHeroAndSectionEntities"
	| "CosmosPageDirectoryEntities"
	| "CosmosPageBlogDetailEntities"
	| "CosmosPagePluginDetailEntities"
	| "CosmosPageLegalContent"
	| "CosmosPageResearch"
	| "CosmosPageEulaKey"
	| "CosmosPageSearch";

/** How to fetch and read one template's structural content. */
interface PageTemplateConfig {
	/** SDK query returning the page with its hero/sections; `null` for templates with no content
	 *  query of their own (redirect), which resolve to a shell from the CosmosPageTemplates probe. */
	contentQuery: PageContentQuery | null;
	/** Response field the query nests the page under (matches the CosmosPageTemplates key). */
	pageField: string;
	/** Fields holding `CosmosReferenceSection` arrays, resolved per-section via CosmosPageRefSection. */
	refSectionFields: readonly string[];
	/** Field holding inline union sections that already carry their concrete typename (no detail fetch). */
	inlineSectionField: string | null;
	/** Field holding article-body components (blog/plugin `articleComponents`), resolved on the query. */
	articleSectionField?: string | null;
	/** Whether the content query takes a `locale` argument (redirect is slug-only). */
	usesLocale: boolean;
}

/**
 * Registry of the page templates the sections API understands, keyed by the CosmosPageTemplates
 * response field (which is also the template key returned by resolvePageTemplate). This mirrors the
 * set of templates the page renderer's templateSelector handles, so any published page routes here
 * — not just CosmosPageHeroAndSection. New templates are added by declaring how to fetch and read
 * their hero/sections, without touching the route logic.
 */
export const PAGE_SECTION_TEMPLATES: Record<string, PageTemplateConfig> = {
	cosmosPageHeroAndSection: {
		contentQuery: "CosmosPageHeroAndSectionEntities",
		pageField: "cosmosPageHeroAndSection",
		refSectionFields: ["refSections"],
		inlineSectionField: null,
		usesLocale: true,
	},
	cosmosPageDirectory: {
		contentQuery: "CosmosPageDirectoryEntities",
		pageField: "cosmosPageDirectory",
		refSectionFields: ["topRefSections", "bottomRefSections"],
		inlineSectionField: null,
		usesLocale: true,
	},
	cosmosPageBlogDetail: {
		// The Entities query resolves the article body (`articleComponents`) inline; ref sections
		// (appended after the article) still resolve per-entity via CosmosPageRefSection.
		contentQuery: "CosmosPageBlogDetailEntities",
		pageField: "cosmosPageBlogDetail",
		refSectionFields: ["refSections"],
		inlineSectionField: null,
		articleSectionField: "articleComponents",
		usesLocale: true,
	},
	cosmosPagePluginDetail: {
		// Same shape as blog detail: the Entities query resolves the article body
		// (`articleComponents`) inline; ref sections still resolve per-entity via CosmosPageRefSection.
		contentQuery: "CosmosPagePluginDetailEntities",
		pageField: "cosmosPagePluginDetail",
		refSectionFields: ["refSections"],
		inlineSectionField: null,
		articleSectionField: "articleComponents",
		usesLocale: true,
	},
	cosmosPageLegalContent: {
		// No *Entities query exists for legal content; its full query already resolves the inline
		// `sections` union (rich-text blocks) with their typenames, so no per-section fetch is needed.
		contentQuery: "CosmosPageLegalContent",
		pageField: "cosmosPageLegalContent",
		refSectionFields: [],
		inlineSectionField: "sections",
		usesLocale: true,
	},
	cosmosPageResearch: {
		// Research pages have a hero but no ref/inline sections (their body is researchCards).
		contentQuery: "CosmosPageResearch",
		pageField: "cosmosPageResearch",
		refSectionFields: [],
		inlineSectionField: null,
		usesLocale: true,
	},
	cosmosPageEulaKey: {
		contentQuery: "CosmosPageEulaKey",
		pageField: "cosmosPageEulaKey",
		refSectionFields: [],
		inlineSectionField: null,
		usesLocale: true,
	},
	cosmosPageSearch: {
		contentQuery: "CosmosPageSearch",
		pageField: "cosmosPageSearch",
		refSectionFields: [],
		inlineSectionField: null,
		usesLocale: true,
	},
	cosmosPageRedirect: {
		contentQuery: null,
		pageField: "cosmosPageRedirect",
		refSectionFields: [],
		inlineSectionField: null,
		usesLocale: false,
	},
};

/** A template match for a slug, as resolved by probing CosmosPageTemplates. */
export interface ResolvedPageTemplate {
	/** The matching CosmosPageTemplates field / PAGE_SECTION_TEMPLATES key, e.g. `cosmosPageDirectory`. */
	key: string;
	typename: string;
	id: string;
	slug: string | null;
}

/**
 * Detect which Cosmos page template serves a slug by probing every template in a single
 * CosmosPageTemplates query — the same mechanism the page renderer's templateSelector (getKey) uses
 * — and returning the first that resolves to a real entry. Returns `null` when nothing matches.
 */
export const resolvePageTemplate = async (
	hygraphSlug: string,
	hygraphLocale: string,
): Promise<ResolvedPageTemplate | null> => {
	const locale = [hygraphLocale, Locale.En] as Locale[];
	const res = await getHygraphCosmosPageData("CosmosPageTemplates", { locale, slug: hygraphSlug }, undefined);

	const record = (res ?? {}) as Record<string, { __typename?: string; id?: string; slug?: string | null } | null>;
	for (const key of Object.keys(record)) {
		const value = record[key];
		if (value?.__typename && value?.id) {
			return { key, typename: value.__typename, id: value.id, slug: value.slug ?? null };
		}
	}
	return null;
};

/** Loosely-typed view of getHygraphCosmosPageData for dynamic (registry-driven) content queries. */
const fetchPageContent = getHygraphCosmosPageData as unknown as (
	contentType: string,
	payload: unknown,
	preview: string | undefined,
) => Promise<Record<string, Record<string, unknown> | null> | null>;

/**
 * Fetch the structural content (hero + sections) of the published page at a slug, whatever its
 * template. Detects the template via CosmosPageTemplates, fetches that template's content query,
 * and normalizes hero + reference/inline sections into a common shape. Applies the site's owner
 * prefix and the tenant ownership guard, returning `null` when no owned published page matches.
 * Published content only — no preview.
 */
export const getPageSectionsData = async (
	slug: string,
	hygraphLocale: string,
): Promise<NormalizedSectionsPage | null> => {
	const hygraphSlug = await resolveOwnerSlug(slug, undefined);

	const template = await resolvePageTemplate(hygraphSlug, hygraphLocale);
	if (!template) {
		return null;
	}
	const config = PAGE_SECTION_TEMPLATES[template.key];
	if (!config) {
		return null;
	}

	// Templates with no content query of their own (redirect) resolve to a shell from the probe.
	if (!config.contentQuery) {
		return {
			id: template.id,
			typename: template.typename,
			slug: template.slug,
			hero: null,
			refSectionEntities: [],
			inlineSections: [],
			articleSections: [],
		};
	}

	const locale = [hygraphLocale, Locale.En] as Locale[];
	const payload = config.usesLocale ? { locale, slug: hygraphSlug } : { slug: hygraphSlug };
	const resp = await fetchPageContent(config.contentQuery, payload, undefined);

	const page = resp?.[config.pageField] ?? null;
	if (!page) {
		return null;
	}
	// Tenant ownership guard: don't surface a foreign-owned page found purely by slug.
	if (!(await isCurrentOwnerPage(page as Parameters<typeof isCurrentOwnerPage>[0], undefined))) {
		return null;
	}

	const refSectionEntities: RefSectionsEntitiesFragment[] = config.refSectionFields.flatMap(
		(field) => (page[field] as RefSectionsEntitiesFragment[] | null | undefined) ?? [],
	);

	// Inline union sections and article-body components arrive already resolved on the page query;
	// keep only entries carrying a typename (the mapper derives variant/tags/id from them).
	const readResolved = (field: string | null | undefined): ResolvedSection[] =>
		field
			? ((page[field] as ResolvedSection[] | null | undefined) ?? []).filter((section) => Boolean(section?.__typename))
			: [];

	return {
		id: (page.id as string | undefined) ?? template.id,
		typename: (page.__typename as string | undefined) ?? template.typename,
		slug: (page.slug as string | undefined) ?? template.slug,
		hero: (page.hero as HeroEntityStub) ?? null,
		refSectionEntities,
		inlineSections: readResolved(config.inlineSectionField),
		articleSections: readResolved(config.articleSectionField),
	};
};
